import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Typography,
  CircularProgress,
  Box,
  Button,
  IconButton,
  Chip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
} from '@mui/material';
import {
  Close as CloseIcon,
  Group as GroupIcon,
  PersonAdd as PersonAddIcon,
  MoreVert as MoreVertIcon,
  AdminPanelSettings as AdminIcon,
  PersonRemove as RemoveIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { Conversation, ChatUser } from '../../types/chat';
import { chatAPI } from '../../services/api';
import ChatAvatar from './ChatAvatar';

interface GroupSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  conversation: Conversation;
  currentUserId: number;
  onConversationUpdate: (conversation: Conversation) => void;
  onLeaveGroup: () => void;
}

// Outlook blue theme
const OUTLOOK_BLUE = '#0078D4';

const GroupSettingsDialog: React.FC<GroupSettingsDialogProps> = ({
  open,
  onClose,
  conversation,
  currentUserId,
  onConversationUpdate,
  onLeaveGroup,
}) => {
  const [groupName, setGroupName] = useState(conversation.group_name || '');
  const [isEditingName, setIsEditingName] = useState(false);
  const [savingName, setSavingName] = useState(false);
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [availableUsers, setAvailableUsers] = useState<ChatUser[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [memberMenuAnchor, setMemberMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<number | null>(null);

  // Check if current user is admin
  const currentUserParticipant = conversation.participants.find(p => p.user_id === currentUserId);
  const isAdmin = currentUserParticipant?.is_admin || false;

  useEffect(() => {
    setGroupName(conversation.group_name || '');
  }, [conversation.group_name]);

  const loadAvailableUsers = async () => {
    setLoadingUsers(true);
    try {
      const users = await chatAPI.getChatUsers();
      // Filter out users already in the group
      const existingIds = new Set(conversation.participants.map(p => p.user_id));
      const available = users.filter(u => !existingIds.has(u.id));
      setAvailableUsers(available);
    } catch (error) {
      // Silent fail
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleSaveGroupName = async () => {
    if (!groupName.trim() || groupName === conversation.group_name) {
      setIsEditingName(false);
      return;
    }

    setSavingName(true);
    try {
      const updated = await chatAPI.updateGroupConversation(conversation.id, { group_name: groupName.trim() });
      onConversationUpdate(updated);
      setIsEditingName(false);
    } catch (error) {
      // Silent fail
    } finally {
      setSavingName(false);
    }
  };

  const handleAddMember = async (userId: number) => {
    try {
      const updated = await chatAPI.addGroupMembers(conversation.id, [userId]);
      onConversationUpdate(updated);
      // Remove from available users
      setAvailableUsers(prev => prev.filter(u => u.id !== userId));
    } catch (error) {
      // Silent fail
    }
  };

  const handleRemoveMember = async (userId: number) => {
    try {
      await chatAPI.removeGroupMember(conversation.id, userId);
      // Update conversation locally
      const updatedParticipants = conversation.participants.filter(p => p.user_id !== userId);
      onConversationUpdate({ ...conversation, participants: updatedParticipants });
    } catch (error) {
      // Silent fail
    }
    handleCloseMemberMenu();
  };

  const handleToggleAdmin = async (userId: number) => {
    try {
      const result = await chatAPI.toggleGroupAdmin(conversation.id, userId);
      // Update conversation locally
      const updatedParticipants = conversation.participants.map(p =>
        p.user_id === userId ? { ...p, is_admin: result.is_admin } : p
      );
      onConversationUpdate({ ...conversation, participants: updatedParticipants });
    } catch (error) {
      // Silent fail
    }
    handleCloseMemberMenu();
  };

  const handleOpenMemberMenu = (event: React.MouseEvent<HTMLElement>, userId: number) => {
    setMemberMenuAnchor(event.currentTarget);
    setSelectedMemberId(userId);
  };

  const handleCloseMemberMenu = () => {
    setMemberMenuAnchor(null);
    setSelectedMemberId(null);
  };

  const handleShowAddMembers = () => {
    setShowAddMembers(true);
    loadAvailableUsers();
  };

  const selectedMember = conversation.participants.find(p => p.user_id === selectedMemberId);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: { borderRadius: 2, maxHeight: '80vh' },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GroupIcon sx={{ color: OUTLOOK_BLUE }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: OUTLOOK_BLUE }}>
            {showAddMembers ? 'Ajouter des membres' : 'Paramètres du groupe'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        {showAddMembers ? (
          // Add members view
          <Box>
            <Button
              startIcon={<CloseIcon />}
              onClick={() => setShowAddMembers(false)}
              sx={{ mb: 2 }}
            >
              Retour
            </Button>

            {loadingUsers ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size={32} sx={{ color: OUTLOOK_BLUE }} />
              </Box>
            ) : availableUsers.length === 0 ? (
              <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', py: 4 }}>
                Aucun utilisateur disponible à ajouter
              </Typography>
            ) : (
              <List>
                {availableUsers.map((user) => (
                  <ListItem key={user.id}>
                    <ListItemAvatar>
                      <ChatAvatar
                        name={user.full_name}
                        isOnline={user.is_online}
                        lastSeenAt={user.last_seen_at}
                        size={40}
                      />
                    </ListItemAvatar>
                    <ListItemText
                      primary={user.full_name}
                      secondary={user.is_online ? 'En ligne' : 'Hors ligne'}
                    />
                    <ListItemSecondaryAction>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleAddMember(user.id)}
                        sx={{
                          borderColor: OUTLOOK_BLUE,
                          color: OUTLOOK_BLUE,
                          '&:hover': { borderColor: '#005a9e', backgroundColor: 'rgba(0,120,212,0.04)' },
                        }}
                      >
                        Ajouter
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        ) : (
          // Main settings view
          <Box>
            {/* Group name section */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                Nom du groupe
              </Typography>
              {isEditingName ? (
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    fullWidth
                    size="small"
                    value={groupName}
                    onChange={(e) => setGroupName(e.target.value)}
                    autoFocus
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveGroupName}
                    disabled={savingName}
                    sx={{ backgroundColor: OUTLOOK_BLUE }}
                  >
                    {savingName ? <CircularProgress size={20} color="inherit" /> : 'OK'}
                  </Button>
                  <Button
                    size="small"
                    onClick={() => {
                      setIsEditingName(false);
                      setGroupName(conversation.group_name || '');
                    }}
                  >
                    Annuler
                  </Button>
                </Box>
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">{conversation.group_name || 'Groupe sans nom'}</Typography>
                  {isAdmin && (
                    <IconButton size="small" onClick={() => setIsEditingName(true)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              )}
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Members section */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                Membres ({conversation.participants.length})
              </Typography>
              {isAdmin && (
                <Button
                  size="small"
                  startIcon={<PersonAddIcon />}
                  onClick={handleShowAddMembers}
                  sx={{ color: OUTLOOK_BLUE }}
                >
                  Ajouter
                </Button>
              )}
            </Box>

            <List sx={{ mx: -2 }}>
              {conversation.participants.map((participant) => (
                <ListItem key={participant.id}>
                  <ListItemAvatar>
                    <ChatAvatar
                      name={participant.user.full_name}
                      isOnline={participant.user.is_online}
                      lastSeenAt={participant.user.last_seen_at}
                      size={40}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {participant.user.full_name}
                        {participant.user_id === currentUserId && (
                          <Chip label="Vous" size="small" sx={{ height: 20, fontSize: '0.7rem' }} />
                        )}
                        {participant.is_admin && (
                          <Chip
                            label="Admin"
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '0.7rem',
                              backgroundColor: OUTLOOK_BLUE,
                              color: 'white',
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={participant.user.is_online ? 'En ligne' : 'Hors ligne'}
                  />
                  {isAdmin && participant.user_id !== currentUserId && (
                    <ListItemSecondaryAction>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenMemberMenu(e, participant.user_id)}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>

            {/* Member action menu */}
            <Menu
              anchorEl={memberMenuAnchor}
              open={Boolean(memberMenuAnchor)}
              onClose={handleCloseMemberMenu}
            >
              <MenuItem onClick={() => selectedMemberId && handleToggleAdmin(selectedMemberId)}>
                <ListItemIcon>
                  <AdminIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {selectedMember?.is_admin ? 'Retirer admin' : 'Rendre admin'}
                </ListItemText>
              </MenuItem>
              <MenuItem
                onClick={() => selectedMemberId && handleRemoveMember(selectedMemberId)}
                sx={{ color: 'error.main' }}
              >
                <ListItemIcon>
                  <RemoveIcon fontSize="small" color="error" />
                </ListItemIcon>
                <ListItemText>Retirer du groupe</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        )}
      </DialogContent>

      {!showAddMembers && (
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={onLeaveGroup}
            color="error"
            variant="outlined"
          >
            Quitter le groupe
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GroupSettingsDialog;
